<template>
  <div>
    <PageHeader :title="'Company'" :heroImg="'/images/heroes/company-hero.webp'" />
    <section id="your-partner" class="py-12 md:py-24" style="background-image: url('/images/partner-bg.webp');">
      <div class="overlay size-full"></div>
      <div class="content max-w-2xl px-6 mx-auto">
        <h2 class="text-left text-dark-gray uppercase text-2xl md:text-4xl sm:text-center">Your Partner in Retail Safety Solutions</h2>
        <div class="mt-8 grid grid-cols-12 gap-8">
          <div v-for="(paragraph, index) in paragraphs" :key="index"
            class="col-span-12 md:col-span-4 md:px-4 flex justify-center items-start mx-auto w-full">
            <p class="font-medium">{{ paragraph }}</p>
          </div>
        </div>
      </div>
    </section>
    <section id="built-for-retail" class="py-12 md:py-24" style="background-image: url('/images/retail-ready-bg.webp');">
      <div class="overlay size-full"></div>
      <div class="content w-full max-w-2xl px-6 mx-auto text-white grid grid-cols-12 gap-8">
        <div class="col-span-12 md:col-span-5 grid gap-3">
          <h2 class="text-white uppercase text-2xl md:text-4xl">Built for Retail</h2>
          <p>We work closely with our customers to understand their shoppers, identify growth opportunities, and create
            programs that improve both sales and consumer satisfaction.</p>
          <div>
            <p>Our retail business is dedicated to helping customers succeed through:</p>
            <ul>
              <li>Purpose built product development</li>
              <li>Data-driven category management</li>
              <li>Retail-ready packaging and merchandising</li>
              <li>Supply chain reliability and on-time delivery</li>
              <li>Private label brand solutions</li>
              <li>Omnichannel support for both in-store and online sales</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section id="brands" style="background-image: url('/images/brands-bg.webp');">
      <div class="brands-title flex flex-col justify-end items-center relative pb-3">
        <div class="bg-graphic flex justify-center items-center h-full">
          <img src="/images/brands-title.svg" class="size-full" />
        </div>
        <div class="text flex flex-col items-center gap-8 md:gap-12 mt-auto">
          <h2 class="uppercase text-white text-center text-2xl md:text-4xl">Our Brands</h2>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <path fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m7 7l5 5l5-5M7 13l5 5l5-5" />
          </svg>
        </div>
      </div>

      <div class="content pt-4 pb-8 px-6 max-w-2xl mx-auto">
        <h3 class="text-white text-center text-2xl md:text-3xl">Retail Brands</h3>
        <div class="flex flex-col gap-y-8 mx-auto max-w-xl pt-8 md:pt-12 pb-12 md:pb-16">
          <div v-for="(row, rowIndex) in brandRows" :key="rowIndex"
            class="flex flex-wrap justify-center md:justify-between items-center gap-x-8 md:gap-x-16 gap-y-8">
            <a v-for="(brand, index) in row" :key="index" :href="brand.href" target="_blank" rel="noopener noreferrer"
              class="inline-flex h-auto max-w-24 md:max-w-32">
              <img :src="brand.src" :alt="brand.alt" class="w-full h-auto object-contain" />
            </a>
          </div>
        </div>
      </div>
    </section>
    <section id="licensed" class="py-12 md:py-24" style="background-image: url('/images/licensed-bg.webp');">
      <div class="max-w-2xl mx-auto px-6">
        <h2 class="text-dark-gray text-center text-2xl md:text-3xl uppercase">Licensed Brands</h2>
        <div class="flex flex-wrap justify-center items-center gap-y-8 gap-x-8 md:gap-x-16 max-w-md mx-auto pt-8 md:pt-12">
          <a v-for="(brand, index) in licensedBrands" :key="index" :href="brand.href" target="_blank"
            rel="noopener noreferrer" class="inline-flex h-auto max-w-24 md:max-w-32">
            <img :src="brand.src" :alt="brand.alt" class="w-full h-auto object-contain" />
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PageHeader from '../PageHeader.vue';

export default {
  name: 'Company',
  data() {
    return {
      paragraphs: [
        'PIP Global Safety is a leading provider of personal protective equipment, helping retailers deliver trusted safety solutions across North America.',
        'Backed by decades of experience in both retail and industrial markets, we combine consumer insights, innovative products, and world-class supply chain capabilities to help our customers grow.',
        'Our focus is simple: deliver the right products, the right brands, and the right retail solutions to drive results.',
      ],
      brandRows: [
        [
          { src: '/images/logos/boss-white.svg', alt: 'Boss logo', href: '/brands/boss' },
          { src: '/images/logos/howard-leight-white.svg', alt: 'howard leight logo', href: '/brands/howard-leight' },
          { src: '/images/logos/prosafety-white.svg', alt: 'pro safety logo', href: '/brands/prosafety' },
          { src: '/images/logos/north-white.svg', alt: 'north logo', href: '/brands/north' },
          { src: '/images/logos/miller-white.svg', alt: 'miller logo', href: '/brands/miller' },
        ],
        [
          { src: '/images/logos/uvex-white.svg', alt: 'uvex logo', href: '/brands/uvex' },
          { src: '/images/logos/puma-white.svg', alt: 'puma logo', href: '/brands/puma' },
          { src: '/images/logos/hearos-white.svg', alt: 'Hearos logo', href: '/brands/hearos' },
          { src: '/images/logos/wcg-white.svg', alt: 'West County Gardener logo', href: '/brands/wcg' },
          { src: '/images/logos/safety-works-white.svg', alt: 'Safety Works logo', href: '/brands/safety-works' },
          { src: '/images/logos/mud-white.svg', alt: 'Mud logo', href: '/brands/mud' },
        ],
      ],
      licensedBrands: [
        { src: '/images/logos/john-deere-black.svg', alt: 'John Deere logo', href: '#' },
        { src: '/images/logos/scrotts-black.svg', alt: 'scotts logo', href: '#' },
        { src: '/images/logos/miracle-gro-black.svg', alt: 'miracle-gro logo', href: '#' },
        { src: '/images/logos/cat-black.svg', alt: 'cat logo', href: '#' },
      ],
    };
  },
  components: {
    PageHeader,
  },
};
</script>

<style lang="scss" scoped>
section {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}


#your-partner {
  position: relative;

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    background: white;
    opacity: 0.85;
    z-index: 1;
  }

  .content {
    position: relative;
    z-index: 2;

    .col-span-12 {
      position: relative;


      &:not(:last-child) {
        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          transform: translateX(1rem);
          width: 1px;
          height: 95%;
          background-color: #000;

          @media (max-width: 768px) {
            top: unset;
            right: unset;
            bottom: 0;
            left: 0;
            transform: translateY(1rem);
            width: 95%;
            height: 1px;
          }
        }
      }

    }
  }
}

#brands {
  position: relative;

  .brands-title {
    overflow: hidden;
    height: 140px;
    position: relative;

    @media (min-width: 768px) {
      height: 210px;
    }

    .text {
      position: relative;
      z-index: 2;
    }

    .bg-graphic {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 800px;
      z-index: 1;

      @media (min-width: 768px) {
        width: 1200px;
      }

      img {
        object-fit: cover;
        object-position: top center;
      }
    }
  }
}


ul {
  list-style-type: disc;
  list-style-position: inside;
  padding-left: 1rem;
}
</style>
